import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { useTracking } from "@/context/TrackingContext";
import axios from "axios";
import { CheckCircle, XCircle, Loader2, Calendar, Clock, User } from "lucide-react";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEGAL_DISCLAIMER = "Este servicio es de acompañamiento y escucha. No es un servicio médico y no sustituye atención profesional.";

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const { trackPurchase } = useTracking();
  const purchaseTracked = useRef(false);
  
  const [status, setStatus] = useState("loading"); // loading, success, error, expired
  const [session, setSession] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 10;
  const pollInterval = 2000;

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus("error");
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (attempts >= maxAttempts) {
      setStatus("error");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/sessions/checkout/status/${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.payment_status === "paid") {
        setStatus("success");
        setSession(response.data.session);
        
        // Track purchase event (only once)
        if (!purchaseTracked.current && response.data.session) {
          purchaseTracked.current = true;
          const sess = response.data.session;
          trackPurchase(
            sess.id,
            sess.listener_id,
            sess.amount_paid || sess.price,
            sess.currency || "USD"
          );
        }
        return;
      } else if (response.data.status === "expired") {
        setStatus("expired");
        return;
      }

      // Continue polling
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (error) {
      console.error("Error checking payment:", error);
      // Continue polling on error
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    }
  };

  return (
    <Layout>
      <div className="min-h-[60vh] flex items-center justify-center py-12 px-4">
        <div className="w-full max-w-lg text-center">
          {status === "loading" && (
            <div className="bg-white rounded-3xl shadow-xl border border-stone-100 p-8">
              <Loader2 className="w-16 h-16 text-primary mx-auto mb-6 animate-spin" />
              <h1 className="text-2xl font-serif font-bold text-foreground">Verificando tu pago...</h1>
              <p className="mt-4 text-stone-600">Por favor espera mientras confirmamos tu reserva.</p>
              <p className="mt-2 text-sm text-stone-400">Intento {attempts + 1} de {maxAttempts}</p>
            </div>
          )}

          {status === "success" && session && (
            <div className="bg-white rounded-3xl shadow-xl border border-stone-100 p-8">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="text-3xl font-serif font-bold text-foreground">¡Pago exitoso!</h1>
              <p className="mt-4 text-stone-600">Tu sesión ha sido confirmada.</p>
              
              <div className="mt-8 bg-stone-50 rounded-2xl p-6 text-left">
                <h2 className="font-semibold text-foreground mb-4">Detalles de tu sesión</h2>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-stone-600">
                    <User className="w-5 h-5 text-primary" />
                    <span>{session.listener_name}</span>
                  </div>
                  <div className="flex items-center gap-3 text-stone-600">
                    <Calendar className="w-5 h-5 text-primary" />
                    <span>{format(parseISO(session.date), "EEEE d 'de' MMMM, yyyy", { locale: es })}</span>
                  </div>
                  <div className="flex items-center gap-3 text-stone-600">
                    <Clock className="w-5 h-5 text-primary" />
                    <span>{session.time} (50 minutos)</span>
                  </div>
                </div>
              </div>

              <p className="mt-6 text-sm text-stone-500">
                Recibirás un email de confirmación con los detalles de tu sesión.
              </p>

              <Button 
                className="mt-6 btn-primary w-full" 
                onClick={() => navigate("/mis-sesiones")}
                data-testid="go-to-sessions-btn"
              >
                Ver mis sesiones
              </Button>

              <p className="mt-6 legal-disclaimer">
                {LEGAL_DISCLAIMER}
              </p>
            </div>
          )}

          {status === "error" && (
            <div className="bg-white rounded-3xl shadow-xl border border-stone-100 p-8">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
              <h1 className="text-2xl font-serif font-bold text-foreground">Error de verificación</h1>
              <p className="mt-4 text-stone-600">
                No pudimos verificar tu pago. Si crees que fue exitoso, por favor revisa tu email o contacta soporte.
              </p>
              <div className="mt-6 flex gap-4 justify-center">
                <Button variant="outline" onClick={() => navigate("/explorar")}>
                  Volver a explorar
                </Button>
                <Button className="btn-primary" onClick={() => navigate("/mis-sesiones")}>
                  Mis sesiones
                </Button>
              </div>
            </div>
          )}

          {status === "expired" && (
            <div className="bg-white rounded-3xl shadow-xl border border-stone-100 p-8">
              <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-amber-600" />
              </div>
              <h1 className="text-2xl font-serif font-bold text-foreground">Sesión de pago expirada</h1>
              <p className="mt-4 text-stone-600">
                El tiempo para completar el pago ha expirado. Por favor intenta de nuevo.
              </p>
              <Button 
                className="mt-6 btn-primary" 
                onClick={() => navigate("/explorar")}
              >
                Explorar listeners
              </Button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
